import numeral from 'numeral';
import Vue from 'vue';

Vue.$config.locales.forEach((item) => {
  numeral.register('locale', item.name, item.format);
});
numeral.locale(Vue.$config.locale);

export function tempNumberFormatter(valueStr, fractionDigits, thousandSeparator, decimalSeparator) {
  if (valueStr == null) return '';

  valueStr = String(valueStr);

  // Normalize decimal separator in input
  const normalizedStr = valueStr.replace(',', '.');

  let [integerPart, decimalPart = ''] = parseFloat(normalizedStr).toFixed(fractionDigits).split('.');

  // Insert thousand separators
  const regex = /(\d)(?=(\d{3})+(?!\d))/g;
  integerPart = integerPart.replace(regex, `$1${thousandSeparator}`);

  // Combine parts
  return fractionDigits > 0
    ? `${integerPart}${decimalSeparator}${decimalPart}`
    : integerPart;
}

export default class NumericUtils {
  static formatNumber(value, pattern) {
    switch (pattern) {
      case 'bytes':
      case 'currency':
      case 'exponential':
      case 'numbers':
      case 'percentages':
      case 'time':
        pattern = Vue.$config.format[pattern];
        break;
      default:
        pattern = pattern || Vue.$config.format.numbers;
    }

    return numeral(value).format(pattern);
  }

  static toNumber(number) {
    if (!number) return 0;
    if (isNaN(number)) {
      const n = number.toString().replaceAll(',', '').replaceAll('.', '');
      return parseFloat(n);
    }
    return parseFloat(number);
  }

  static toString(number, thousandsSeparator = '.', decimalSeparator = ',', decimalPlaces = 0) {
    if (number == null || isNaN(number)) return '0';

    const roundedNumber = Number(number).toFixed(decimalPlaces);

    const [intPartRaw, decimalPartRaw] = roundedNumber.split('.');

    const isNegative = intPartRaw.startsWith('-');
    const intPart = isNegative ? intPartRaw.slice(1) : intPartRaw;

    const formattedInt = intPart
      .split('')
      .reverse()
      .reduce((acc, digit, idx) => digit + ((idx && idx % 3 === 0) ? thousandsSeparator : '') + acc, '');

    const result = (isNegative ? '-' : '') + formattedInt;

    return decimalPlaces > 0 ? `${result}${decimalSeparator}${decimalPartRaw}` : result;
  }

  static formatAmount(number, decimalPlaces = 0) {
    const pattern = `0,0.${Array(decimalPlaces).fill('0').join('')}`;
    return this.formatNumber(number, pattern);
  }

/**
 * Formats a number to a specified decimal precision, truncating any extra decimal places.
 * 
 * @param {number} value - The number to format
 * @param {number} decimalPlaces - Number of decimal places to keep (default: 0)
 * @returns {number} The formatted number with specified decimal precision
 * @throws {Error} If parameters are invalid
 */
  static formatAmountAsNumber(value, decimalPlaces = 0) {
    // Input validation
    if (value === null || value === undefined) {
      // throw new Error('Input value cannot be null or undefined');
      return NaN;
    }

    // Handle edge case to avoid potential overflow
    if (decimalPlaces === 0) {
      return Math.round(value);
    }

    const parts = value.toString().split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1] || '';
    // check if value already has desired precision
    if (decimalPart.length <= decimalPlaces) {
      return value;
    }

    // Use a more robust approach to handle decimal precision
    // This avoids floating-point errors better than the original approach
    const factor = 10 ** decimalPlaces;

    // For large numbers, avoid potential overflow by using string operations
    if (Math.abs(value) > Number.MAX_SAFE_INTEGER / factor) {
      // For extremely large numbers, fall back to string-based handling
      const truncatedDecimalPart = decimalPart.slice(0, decimalPlaces);
      const paddedDecimalPart = truncatedDecimalPart.padEnd(decimalPlaces, '0');
      return Number(`${integerPart}.${paddedDecimalPart}`);
    }

    // Standard case: multiply, truncate, then divide
    return Math.round(value * factor) / factor;
  }

  static animateNumber(finalNumber, duration = 5000, startNumber = 0, callback) {
    const startTime = performance.now();
    function updateNumber(currentTime) {
      const elapsedTime = currentTime - startTime;
      if (elapsedTime > duration) {
        callback(finalNumber);
      } else {
        const rate = elapsedTime / duration;
        const currentNumber = Math.round(rate * finalNumber);
        callback(currentNumber);
        requestAnimationFrame(updateNumber);
      }
    }
    requestAnimationFrame(updateNumber);
  }
}

Vue.filter('formatAmount', (value, pattern) => NumericUtils.formatAmount(value, pattern));
Vue.filter('formatAmountAsNumber', (value, pattern) => NumericUtils.formatAmountAsNumber(value, pattern));
Vue.filter('formatNumber', (value, pattern) => NumericUtils.formatNumber(value, pattern));
Vue.filter('formatCurrency', value => NumericUtils.formatNumber(value, 'currency'));
Vue.filter('toString', (value, thousandsSeparator, decimalSeparator) => NumericUtils.toString(value, thousandsSeparator, decimalSeparator));



// WEBPACK FOOTER //
// ./src/utils/numeric.js